import { Badge, Button, Input } from "@norican/react";
import { useState } from "react";

import { Notice } from "@/components/page";
import { PersonSearch } from "@/components/person-search";
import { subText, table, td, th } from "@/components/table";
import {
  type AdhocDraft,
  type OrgPerson,
  type ScenarioSummary,
  useConfirmOrgAdhoc,
  useCreateOrgAdhoc,
  useOrgAdhoc,
  useOrgScenarios,
  useUndoOrgScenario,
} from "@/lib/api";
import { dateInZone, errorMessage } from "@/lib/format";
import { FIELD_LABEL } from "@/lib/org";

/**
 * Change one account on the spot — title, manager, department or company.
 *
 * The same four attributes, the same AD service account and the same worker
 * as a scenario; what differs is that it is one person and one decision.
 * Timo asked for it on 2 Oct 2026 and chose self-service over four eyes, so
 * the design puts everything into making the one decision an informed one:
 *
 * 1. **Prepare** builds the change and shows every old → new pair, with names
 *    rather than object ids, read from the directory snapshot. Nothing is
 *    written.
 * 2. **Confirm** is a separate, deliberate step on a panel that says in words
 *    that it writes to Active Directory, under the person's own reason for
 *    the change. Only then does the worker get it.
 *
 * Discarding at step 2 is a plain delete, because nothing has been written.
 */

const WRITABLE = [
  "job_title",
  "department",
  "company_name",
  "office_location",
  "business_category",
  "country",
  "city",
] as const;
type Writable = (typeof WRITABLE)[number];

export function AdhocPanel({ people, enabled }: { people: OrgPerson[]; enabled: boolean }) {
  // The id of the prepared-but-unconfirmed change, if there is one. Holding
  // the id rather than the draft means a reload lands back on the same
  // confirmation rather than a blank form.
  const [pending, setPending] = useState<string | null>(null);
  return (
    <section className="mt-6">
      {pending ? (
        <AdhocConfirm id={pending} onDone={() => setPending(null)} onPrepared={setPending} />
      ) : (
        <AdhocForm people={people} enabled={enabled} onPrepared={setPending} />
      )}
      <AdhocHistory enabled={enabled} onPrepared={setPending} />
    </section>
  );
}

function AdhocForm({
  people,
  enabled,
  onPrepared,
}: {
  people: OrgPerson[];
  enabled: boolean;
  onPrepared: (id: string) => void;
}) {
  const create = useCreateOrgAdhoc();
  const [who, setWho] = useState<OrgPerson | null>(null);
  const [manager, setManager] = useState<OrgPerson | null>(null);
  const [values, setValues] = useState<Record<Writable, string>>({
    job_title: "",
    department: "",
    company_name: "",
    office_location: "",
    business_category: "",
    country: "",
    city: "",
  });
  const [reason, setReason] = useState("");

  const current = (f: Writable) => (who ? (who[f] ?? "") : "");
  // Only fields the person actually changed travel to the server. A field
  // left at today's value is not a change, and the server says so anyway.
  const edited = WRITABLE.filter((f) => values[f].trim() && values[f].trim() !== current(f));
  const managerChanged = Boolean(manager && who && manager.id !== who.manager_id);
  const ready = who && reason.trim().length >= 3 && (edited.length > 0 || managerChanged);

  // `manager` is seeded with today's manager rather than left null and filled
  // in from `who` at render time. With a fallback in the value, clearing it
  // put the current manager straight back and the "change" link looked dead.
  const pick = (p: OrgPerson | null) => {
    setWho(p);
    setManager(p ? (people.find((x) => x.id === p.manager_id) ?? null) : null);
    setValues({
      job_title: p?.job_title ?? "",
      department: p?.department ?? "",
      company_name: p?.company_name ?? "",
      office_location: p?.office_location ?? "",
      business_category: p?.business_category ?? "",
      country: p?.country ?? "",
      city: p?.city ?? "",
    });
  };

  if (!enabled) {
    return (
      <Notice>
        Changing an account on the spot is limited to a few named people. You can still prepare a
        scenario.
      </Notice>
    );
  }
  return (
    <div className="border-border mt-3 rounded border p-4">
      <div className="flex flex-wrap items-end gap-4">
        <PersonSearch people={people} label="Person" value={who} onChange={pick} />
        {who ? (
          <>
            {WRITABLE.map((f) => (
              <label key={f} className="text-fg-muted flex flex-col text-xs">
                {FIELD_LABEL[f]}
                <Input
                  value={values[f]}
                  onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                />
              </label>
            ))}
            <PersonSearch people={people} label="Manager" value={manager} onChange={setManager} />
          </>
        ) : null}
      </div>
      {who ? (
        <>
          <label className="text-fg-muted mt-4 flex flex-col text-xs">
            Reason — why this is being changed
            <Input
              placeholder="Promotion effective 1 November, agreed with HR"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <div className="mt-4 flex items-center gap-3">
            <Button
              size="sm"
              disabled={!ready || create.isPending}
              onClick={() =>
                create.mutate(
                  {
                    subject_id: who.id,
                    reason: reason.trim(),
                    manager: managerChanged ? manager!.id : null,
                    ...Object.fromEntries(edited.map((f) => [f, values[f].trim()])),
                  },
                  { onSuccess: (d) => d && onPrepared(d.scenario.id) },
                )
              }
            >
              {create.isPending ? "Preparing…" : "Prepare change"}
            </Button>
            <span className={subText}>
              Nothing is written yet. The next screen shows exactly what would change.
            </span>
          </div>
        </>
      ) : null}
      {create.error ? (
        <p className="text-danger-fg mt-2 text-sm">{errorMessage(create.error)}</p>
      ) : null}
    </div>
  );
}

/**
 * The write confirmation. Everything on it is read back from the server, not
 * carried over from the form, so what is confirmed is what was stored.
 */
function AdhocConfirm({
  id,
  onDone,
  onPrepared,
}: {
  id: string;
  onDone: () => void;
  //: Where to go when an undo prepared from here produces a new draft.
  onPrepared?: (id: string) => void;
}) {
  const { data, isLoading, error } = useOrgAdhoc(id);
  const confirm = useConfirmOrgAdhoc();
  if (isLoading) {
    return <Notice>Loading…</Notice>;
  }
  if (error || !data) {
    return <Notice tone="danger">{errorMessage(error)}</Notice>;
  }
  const draft = data.scenario.status === "draft";
  return (
    <div
      className={`mt-3 rounded border p-4 ${draft ? "border-warning-border bg-warning-bg" : "border-border"}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-fg text-sm font-semibold">
          {draft ? "Confirm: this writes to Active Directory" : "Sent to Active Directory"}
        </h3>
        <AdhocState scenario={data.scenario} />
      </div>
      <p className="text-fg mt-2 text-sm">
        {data.subject.display_name}
        <span className={`${subText} ml-2`}>{data.subject.upn}</span>
      </p>
      <AdhocChanges data={data} />
      <p className={`${subText} mt-3`}>Reason: {data.scenario.reason}</p>
      {draft ? (
        <>
          <p className="text-fg mt-3 text-sm">
            Confirming writes these values to the account in Disagroup.net and then to Entra. It is
            logged under your name. There is no second approval after this.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <Button size="sm" disabled={confirm.isPending} onClick={() => confirm.mutate(id)}>
              {confirm.isPending ? "Sending…" : "Confirm and write to AD"}
            </Button>
            <Button size="sm" variant="ghost" onClick={onDone}>
              Not now
            </Button>
          </div>
        </>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button size="sm" variant="outline" onClick={onDone}>
            Done
          </Button>
          <UndoButton
            scenarioId={String(data.scenario.id)}
            status={data.scenario.status}
            writtenCount={data.scenario.written_count ?? 0}
            onPrepared={onPrepared}
          />
        </div>
      )}
      {confirm.error ? (
        <p className="text-danger-fg mt-2 text-sm">{errorMessage(confirm.error)}</p>
      ) : null}
    </div>
  );
}

const AD_STATE: Record<string, string> = {
  pending: "queued",
  written: "written to AD",
  already: "already that value",
  drift: "refused: AD has moved since",
  refused: "refused",
  failed: "failed",
};

function AdhocChanges({ data }: { data: AdhocDraft }) {
  return (
    <table className={`${table} mt-3`}>
      <thead>
        <tr>
          <th className={th}>Field</th>
          <th className={th}>Now</th>
          <th className={th}>After</th>
          <th className={th}>AD</th>
        </tr>
      </thead>
      <tbody>
        {data.changes.map((c) => {
          const label = data.labels[c.field] ?? { old: c.old, new: c.new };
          return (
            <tr key={c.id}>
              <td className={td}>{FIELD_LABEL[c.field] ?? c.field}</td>
              <td className={`${td} text-fg-muted`}>{label.old ?? "—"}</td>
              <td className={`${td} font-medium`}>{label.new}</td>
              <td className={td}>
                {AD_STATE[c.ad_state ?? "pending"] ?? c.ad_state}
                {c.verified_at ? <div className={subText}>verified in Entra</div> : null}
                {c.ad_detail ? <div className={subText}>{c.ad_detail}</div> : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function AdhocState({ scenario }: { scenario: ScenarioSummary }) {
  const tone =
    scenario.status === "applied"
      ? "success"
      : scenario.status === "partial"
        ? "danger"
        : scenario.status === "applying"
          ? "warning"
          : "neutral";
  const label =
    scenario.status === "draft"
      ? "not confirmed"
      : scenario.status === "applying"
        ? "writing, next worker pass"
        : scenario.status === "applied"
          ? "done and verified"
          : scenario.status;
  return (
    <Badge tone={tone} variant="subtle">
      {label}
    </Badge>
  );
}

/** What has been changed this way, most recent first. */
function AdhocHistory({
  enabled,
  onPrepared,
}: {
  enabled: boolean;
  onPrepared?: (id: string) => void;
}) {
  const list = useOrgScenarios(enabled, true, "adhoc");
  const rows = (list.data ?? []).filter((s) => s.status !== "draft");
  const [open, setOpen] = useState<string | null>(null);
  if (!rows.length) {
    return null;
  }
  return (
    <div className="mt-6">
      <h3 className="text-fg-muted text-xs font-medium">Recent ad-hoc changes</h3>
      <table className={`${table} mt-2`}>
        <thead>
          <tr>
            <th className={th}>Account</th>
            <th className={th}>Reason</th>
            <th className={th}>State</th>
            <th className={th}>By</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr
              key={s.id}
              className="hover:bg-bg-subtle cursor-pointer"
              onClick={() => setOpen(open === s.id ? null : s.id)}
            >
              <td className={`${td} font-medium`}>{s.name.replace(/^Ad-hoc: /, "")}</td>
              <td className={td}>{s.reason}</td>
              <td className={td}>
                <AdhocState scenario={s} />
              </td>
              <td className={td}>
                {dateInZone(s.created_at)}
                <div className={subText}>{s.created_by_upn}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {open ? (
        <AdhocConfirm
          id={open}
          onDone={() => setOpen(null)}
          onPrepared={(next) => {
            setOpen(null);
            onPrepared?.(next);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Put a change that reached AD back.
 *
 * Deliberately not a one-click button. It asks why, exactly like the change
 * it reverses, and what it produces is a normal draft that is then confirmed
 * on the ordinary screen. Undo is another write, not a way around the write
 * path — which also means the drift check still applies, so an attribute
 * somebody has edited since is refused rather than overwritten.
 */
export function UndoButton({
  scenarioId,
  status,
  writtenCount,
  onPrepared,
}: {
  scenarioId: string;
  status: string;
  writtenCount: number;
  onPrepared?: (id: string) => void;
}) {
  const undo = useUndoOrgScenario();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [blocked, setBlocked] = useState<UndoBlocked[]>([]);

  // Only once something actually landed, and never while the worker has it.
  if (!writtenCount || status === "testing" || status === "applying") {
    return null;
  }
  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Undo
      </Button>
    );
  }
  return (
    <div className="border-border mt-3 w-full rounded border p-4">
      <h3 className="text-fg text-sm font-semibold">Undo this change</h3>
      <p className={`${subText} mt-1`}>
        Prepares the reverse of the {writtenCount} value{writtenCount === 1 ? "" : "s"} that reached
        AD. Nothing is written until you confirm the reversal on the next screen.
      </p>
      <label className="text-fg-muted mt-3 flex flex-col text-xs">
        Reason — why this is being put back
        <Input
          placeholder="Wrong person picked"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <div className="mt-3 flex items-center gap-3">
        <Button
          size="sm"
          disabled={reason.trim().length < 3 || undo.isPending}
          onClick={() =>
            undo.mutate(
              { id: scenarioId, reason: reason.trim() },
              {
                onSuccess: (d) => {
                  if (!d) {
                    return;
                  }
                  setOpen(false);
                  setReason("");
                  setBlocked(d.not_restorable ?? []);
                  onPrepared?.(d.scenario.id);
                },
              },
            )
          }
        >
          {undo.isPending ? "Preparing…" : "Prepare the undo"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      {undo.error ? (
        <p className="text-danger-fg mt-2 text-sm">{errorMessage(undo.error)}</p>
      ) : null}
      <UndoBlockedList rows={blocked} />
    </div>
  );
}

type UndoBlocked = { field?: string; subject_upn?: string | null; detail?: string };

/** What the reversal could not put back. Shown, never dropped quietly. */
function UndoBlockedList({ rows }: { rows: UndoBlocked[] }) {
  if (!rows.length) {
    return null;
  }
  return (
    <Notice tone="danger">
      <p className="text-sm">
        {rows.length} attribute{rows.length === 1 ? "" : "s"} cannot be put back, because
        {rows.length === 1 ? " it was" : " they were"} empty before the change and HROps can only
        write values, not clear them. Clear in AD by hand if that is what should happen:
      </p>
      <ul className="mt-1 list-disc pl-5 text-sm">
        {rows.map((r, i) => (
          <li key={`${r.field}-${i}`}>
            {FIELD_LABEL[r.field ?? ""] ?? r.field}
            {r.subject_upn ? ` on ${r.subject_upn}` : ""}
          </li>
        ))}
      </ul>
    </Notice>
  );
}
